import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu as AntMenu, Button, Drawer, Space, type MenuProps } from 'antd';
import {
  BarChartOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
  AppstoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  UserOutlined,
  MenuOutlined,
} from '@ant-design/icons';

import { APP_ROUTES, MANAGER_ROUTES, MENU_CONFIG } from '../constants';
import { MENU_TEXT } from '../constants/text';
import { useIsMobile } from '../hooks/useIsMobile';
import ConnectSorareButton from './ConnectSorareButton';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import styles from './Menu.module.css';

import type { MenuComponentProps } from '../types/props';

const SearchModal = lazy(() => import('./SearchModal'));
const RefreshCardsModal = lazy(() => import('./RefreshCardsModal'));

const Menu = (props: MenuComponentProps) => {
  const { location } = props;

  const [isSearchModalVisible, setSearchModalVisible] = useState(false);
  const [isRefreshCardsModalVisible, setRefreshCardsModalVisible] = useState(false);
  const [isNavDrawerOpen, setNavDrawerOpen] = useState(false);
  const isMobile = useIsMobile();

  const items: NonNullable<MenuProps['items']> = [
    {
      label: <Link to={APP_ROUTES.DASHBOARD}>{MENU_TEXT.DASHBOARD}</Link>,
      key: APP_ROUTES.DASHBOARD,
      icon: <BarChartOutlined />,
    },
    {
      label: <Link to={APP_ROUTES.AUCTIONS}>{MENU_TEXT.AUCTIONS}</Link>,
      key: APP_ROUTES.AUCTIONS,
      icon: <ClockCircleOutlined />,
    },
    {
      label: <Link to={APP_ROUTES.MARKET}>{MENU_TEXT.MARKET}</Link>,
      key: APP_ROUTES.MARKET,
      icon: <ShoppingCartOutlined />,
    },
    {
      label: <Link to={APP_ROUTES.INSTANT_BUY}>{MENU_TEXT.INSTANT_BUY}</Link>,
      key: APP_ROUTES.INSTANT_BUY,
      icon: <ShoppingCartOutlined />,
    },
    {
      label: <Link to={APP_ROUTES.ALL_CARDS}>{MENU_TEXT.ALL_CARDS}</Link>,
      key: APP_ROUTES.ALL_CARDS,
      icon: <AppstoreOutlined />,
    },
    {
      label: <Link to={APP_ROUTES.TOP_PLAYERS}>{MENU_TEXT.TOP_PLAYERS}</Link>,
      key: APP_ROUTES.TOP_PLAYERS,
      icon: <OrderedListOutlined />,
    },
  ];

  if (location?.pathname?.startsWith(APP_ROUTES.MANAGER)) {
    const managerSlug = location.pathname.split('/')[2];
    items.push({
      label: 'Manager',
      key: 'manager',
      icon: <UserOutlined />,
      children: [
        {
          label: (
            <Link to={`${APP_ROUTES.MANAGER}/${managerSlug}${MANAGER_ROUTES.CARDS}`}>
              Football Cards
            </Link>
          ),
          key: `${APP_ROUTES.MANAGER}/${managerSlug}${MANAGER_ROUTES.CARDS}`,
        },
        {
          label: (
            <Link to={`${APP_ROUTES.MANAGER}/${managerSlug}${MANAGER_ROUTES.CARDS}`}>
              Refresh All Cards
            </Link>
          ),
          key: `${APP_ROUTES.MANAGER}/${managerSlug}${MANAGER_ROUTES.REFRESH_CARDS}`,
        },
      ],
    });
  }

  items.push({
    label: 'Search',
    key: APP_ROUTES.SEARCH,
    icon: <SearchOutlined />,
  });

  const onMenuOptionClicked: MenuProps['onClick'] = (info) => {
    const { key } = info;
    if (key === APP_ROUTES.SEARCH) {
      setSearchModalVisible(true);
    } else if (key.startsWith(APP_ROUTES.MANAGER) && key.endsWith(MANAGER_ROUTES.REFRESH_CARDS)) {
      setRefreshCardsModalVisible(true);
    }
    // On mobile, any selection (navigation, Search, or Refresh) also closes the nav drawer.
    if (isMobile) {
      setNavDrawerOpen(false);
    }
  };

  const renderAntMenu = (mode: 'horizontal' | 'inline') => (
    <AntMenu
      selectedKeys={[location.pathname]}
      mode={mode}
      items={items}
      onClick={onMenuOptionClicked}
      subMenuCloseDelay={MENU_CONFIG.SUB_MENU_CLOSE_DELAY}
      className={mode === 'horizontal' ? styles.desktopMenu : undefined}
    />
  );

  return (
    <>
      {isMobile ? (
        <>
          <div className={styles.mobileBar}>
            <Button
              type="text"
              aria-label="Open navigation"
              className={styles.hamburger}
              icon={<MenuOutlined />}
              onClick={() => setNavDrawerOpen(true)}
              data-testid="sa-nav-drawer-trigger"
            />
            <Link to={APP_ROUTES.DASHBOARD} className={styles.logoLink} aria-label="SorareAce home">
              <Logo compact />
            </Link>
          </div>
          <Drawer
            title={<Logo />}
            placement="left"
            open={isNavDrawerOpen}
            onClose={() => setNavDrawerOpen(false)}
            extra={
              <Space>
                <ConnectSorareButton />
                <ThemeToggle />
              </Space>
            }
          >
            {renderAntMenu('inline')}
          </Drawer>
        </>
      ) : (
        <div className={styles.desktopBar}>
          <Link to={APP_ROUTES.DASHBOARD} className={styles.logoLink} aria-label="SorareAce home">
            <Logo />
          </Link>
          {renderAntMenu('horizontal')}
          <ConnectSorareButton />
          <ThemeToggle />
        </div>
      )}
      {isSearchModalVisible && (
        <Suspense fallback={null}>
          <SearchModal isVisible={isSearchModalVisible} setVisible={setSearchModalVisible} />
        </Suspense>
      )}
      {/* Slug precedence: this modal belongs to the Manager submenu's "Refresh All Cards" entry
          and stays keyed off the PATHNAME, so refreshing while browsing /manager/other-guy/cards
          refreshes other-guy. The connected user's own "Refresh my cards" lives in
          ConnectSorareButton with its own state + the SESSION slug — deliberately not shared. */}
      {isRefreshCardsModalVisible && (
        <Suspense fallback={null}>
          <RefreshCardsModal
            isVisible={isRefreshCardsModalVisible}
            setVisible={setRefreshCardsModalVisible}
            managerSlug={location.pathname?.split('/')?.[2] ?? ''}
          />
        </Suspense>
      )}
    </>
  );
};

export default Menu;
